import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect, useCallback } from 'react';
import { getAuthHeaders } from '../utils/authHeaders';
import { Badge, tableStyle, thStyle, tdStyle, inputStyle, btnPrimary, btnSecondary } from './CommissionOverview';
import { FormRow, overlayStyle, modalStyle } from './CommissionBeneficiaries';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const STATUSES = ['draft', 'pending_approval', 'approved', 'scheduled', 'partially_paid', 'paid', 'cancelled', 'reopened'];

export default function CommissionSettlements() {
  const companyId = localStorage.getItem('selectedCompanyId');
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [status, setStatus] = useState('');
  const [eligible, setEligible] = useState([]);
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [detail, setDetail] = useState(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/companies/${companyId}/commissions/settlements?per_page=50`;
      if (status) url += `&status=${status}`;
      const res = await fetch(url, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed');
      const json = await res.json();
      setList(Array.isArray(json.data) ? json.data : []);
    } catch (e) { console.error(e); setList([]); }
    setLoading(false);
  }, [companyId, status]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const openCreate = async () => {
    try {
      const res = await fetch(`${API}/companies/${companyId}/commissions/settlements/eligible-entries`, { headers: getAuthHeaders() });
      const j = res.ok ? await res.json() : [];
      setEligible(Array.isArray(j) ? j : []);
    } catch (e) { setEligible([]); }
    try {
      const res2 = await fetch(`${API}/companies/${companyId}/commissions/beneficiaries?per_page=200`, { headers: getAuthHeaders() });
      const j2 = res2.ok ? await res2.json() : { data: [] };
      setBeneficiaries(Array.isArray(j2.data) ? j2.data : []);
    } catch (e) { setBeneficiaries([]); }
    setShowCreate(true);
  };

  const openDetail = async (id) => {
    try {
      const res = await fetch(`${API}/companies/${companyId}/commissions/settlements/${id}`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed');
      setDetail(await res.json());
    } catch (e) { console.error(e); }
  };

  const refreshDetail = (id) => { openDetail(id); fetchList(); };

  const fmt = (v) => '$' + Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2 });

  return (
    <div style={{ padding: '24px 32px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#1a1a2e' }}>🏦 Commission Settlements</h2>
        <button style={btnPrimary} onClick={openCreate}>+ New Settlement</button>
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
        <select value={status} onChange={e => setStatus(e.target.value)} style={{ ...inputStyle, width: 180 }}>
          <option value="">All Statuses</option>
          {STATUSES.map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
        </select>
      </div>

      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead><tr>{['Settlement #', 'Batch', 'Beneficiary', 'Gross', 'Final', 'Paid', 'Remaining', 'Status', ''].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
            <tbody>
              {initialLoading && <tr><td colSpan={9} style={{ ...tdStyle, textAlign: 'center' }}>Loading...</td></tr>}
              {!loading && list.length === 0 && <tr><td colSpan={9} style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af' }}>No settlements yet</td></tr>}
              {list.map(s => (
                <tr key={s.id}>
                  <td style={{ ...tdStyle, fontWeight: 600 }}>{s.settlement_number}</td>
                  <td style={tdStyle}>{s.batch?.batch_number || '—'}</td>
                  <td style={tdStyle}>{s.beneficiary?.display_name || '—'}</td>
                  <td style={tdStyle}>{fmt(s.gross_amount)}</td>
                  <td style={tdStyle}>{fmt(s.final_amount)}</td>
                  <td style={tdStyle}>{fmt(s.paid_amount)}</td>
                  <td style={tdStyle}>{fmt(s.remaining_amount)}</td>
                  <td style={tdStyle}><Badge value={s.status} /></td>
                  <td style={tdStyle}><button style={btnSecondary} onClick={() => openDetail(s.id)}>View</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showCreate && (
        <CreateSettlementModal companyId={companyId} eligible={eligible} beneficiaries={beneficiaries}
          onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); fetchList(); }} />
      )}

      {detail && (
        <SettlementDetailModal companyId={companyId} detail={detail} fmt={fmt}
          onClose={() => setDetail(null)} onChanged={() => refreshDetail(detail.id)} />
      )}
    </div>
  );
}

function CreateSettlementModal({ companyId, eligible, beneficiaries, onClose, onCreated }) {
  const [mode, setMode] = useState('batch');
  const [beneficiaryId, setBeneficiaryId] = useState('');
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const filtered = mode === 'individual' && beneficiaryId ? eligible.filter(e => String(e.beneficiary_id) === String(beneficiaryId)) : eligible;

  const toggle = (id) => setSelected(sel => sel.includes(id) ? sel.filter(x => x !== id) : [...sel, id]);

  const submit = async () => {
    if (selected.length === 0) { setErr('Select at least one entry'); return; }
    if (mode === 'individual' && !beneficiaryId) { setErr('Select a beneficiary'); return; }
    setSaving(true); setErr('');
    try {
      const url = mode === 'batch' ? `${API}/companies/${companyId}/commissions/settlements/batch` : `${API}/companies/${companyId}/commissions/settlements/individual`;
      const body = mode === 'batch' ? { entry_ids: selected } : { beneficiary_id: beneficiaryId, entry_ids: selected };
      const res = await fetch(url, { method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      if (!res.ok) { const j = await res.json().catch(() => ({})); throw new Error(j.message || 'Failed to create'); }
      onCreated();
    } catch (e) { setErr(e.message); }
    setSaving(false);
  };

  const fmt = (v) => '$' + Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2 });

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={{ ...modalStyle, width: 620 }} onClick={e => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>New Settlement</h3>
        {err && <div style={{ color: '#ef4444', fontSize: 13, marginBottom: 10 }}>{err}</div>}
        <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
          <FormRow label="Mode">
            <select value={mode} onChange={e => { setMode(e.target.value); setSelected([]); }} style={inputStyle}>
              <option value="batch">Batch (multi-beneficiary)</option>
              <option value="individual">Individual (single beneficiary)</option>
            </select>
          </FormRow>
          {mode === 'individual' && (
            <FormRow label="Beneficiary">
              <select value={beneficiaryId} onChange={e => { setBeneficiaryId(e.target.value); setSelected([]); }} style={inputStyle}>
                <option value="">Select...</option>
                {beneficiaries.map(b => <option key={b.id} value={b.id}>{b.display_name}</option>)}
              </select>
            </FormRow>
          )}
        </div>
        <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Eligible Due Entries</div>
        <div style={{ maxHeight: 260, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 8 }}>
          <table style={tableStyle}>
            <thead><tr>{['', 'Entry #', 'Beneficiary', 'Remaining', 'Due Date'].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
            <tbody>
              {filtered.length === 0 && <tr><td colSpan={5} style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af' }}>No eligible entries</td></tr>}
              {filtered.map(en => (
                <tr key={en.id}>
                  <td style={tdStyle}><input type="checkbox" checked={selected.includes(en.id)} onChange={() => toggle(en.id)} /></td>
                  <td style={tdStyle}>{en.entry_number}</td>
                  <td style={tdStyle}>{en.beneficiary?.display_name || '—'}</td>
                  <td style={tdStyle}>{fmt(en.remaining_amount)}</td>
                  <td style={tdStyle}>{en.due_date}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <button style={btnSecondary} onClick={onClose}>Cancel</button>
          <button style={btnPrimary} onClick={submit} disabled={saving}>{saving ? 'Creating...' : 'Create Settlement'}</button>
        </div>
      </div>
    </div>
  );
}

export function SettlementDetailModal({ companyId, detail, fmt, onClose, onChanged }) {
  const [adjustAmount, setAdjustAmount] = useState('');
  const [adjustReason, setAdjustReason] = useState('');
  const [scheduleDate, setScheduleDate] = useState('');
  const [payAmount, setPayAmount] = useState('');
  const [payDate, setPayDate] = useState('');
  const [payMethod, setPayMethod] = useState('');
  const [payReference, setPayReference] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const canPay = ['approved', 'scheduled', 'partially_paid'].includes(detail.status) && Number(detail.remaining_amount) > 0;
  const validPayment = Number(payAmount) > 0 && Number(payAmount) <= Number(detail.remaining_amount) && Boolean(payDate);

  const call = async (path, body) => {
    setBusy(true); setErr('');
    try {
      const res = await fetch(`${API}/companies/${companyId}/commissions/settlements/${detail.id}${path}`, {
        method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify(body || {}),
      });
      if (!res.ok) { const j = await res.json().catch(() => ({})); throw new Error(j.message || 'Action failed'); }
      setBusy(false);
      return true;
    } catch (e) { setErr(e.message); setBusy(false); return false; }
  };

  const doApprove = async () => { if (await call('/approve')) onChanged(); };
  const doReject = async () => { if (await call('/reject', { reason: window.prompt('Rejection reason:') || '' })) onChanged(); };
  const doAdjust = async () => { if (await call('/adjust', { adjustment_amount: adjustAmount, reason: adjustReason })) onChanged(); };
  const doSchedule = async () => { if (await call('/schedule', { scheduled_date: scheduleDate })) onChanged(); };
  const doPay = async () => { if (await call('/pay', { amount: payAmount, payment_date: payDate, payment_method: payMethod, reference: payReference })) onChanged(); };
  const doCancel = async () => { if (await call('/cancel', { reason: window.prompt('Cancellation reason:') || '' })) onChanged(); };
  const doReopen = async () => { if (await call('/reopen', { reason: window.prompt('Reopen reason:') || '' })) onChanged(); };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={{ ...modalStyle, width: 680 }} onClick={e => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>{detail.settlement_number} <Badge value={detail.status} /></h3>
        {err && <div style={{ color: '#ef4444', fontSize: 13, marginBottom: 10 }}>{err}</div>}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 16 }}>
          <Info label="Beneficiary" value={detail.beneficiary?.display_name || '—'} />
          <Info label="Gross" value={fmt(detail.gross_amount)} />
          <Info label="Adjustment" value={fmt(detail.adjustment_amount)} />
          <Info label="Final" value={fmt(detail.final_amount)} />
          <Info label="Paid" value={fmt(detail.paid_amount)} />
          <Info label="Remaining" value={fmt(detail.remaining_amount)} />
          <Info label="Payment Status" value={<Badge value={detail.payment_status} />} />
          <Info label="Scheduled Date" value={detail.scheduled_date || '—'} />
          <Info label="Negative Balance Handling" value={detail.negative_balance_handling || '—'} />
        </div>

        {Array.isArray(detail.entry_links) && detail.entry_links.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <h4 style={{ marginBottom: 6 }}>Entries</h4>
            <table style={tableStyle}>
              <thead><tr>{['Entry #', 'Allocated', 'Status'].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
              <tbody>
                {detail.entry_links.map(l => (
                  <tr key={l.id}>
                    <td style={tdStyle}>{l.entry?.entry_number || '—'}</td>
                    <td style={tdStyle}>{fmt(l.allocated_amount)}</td>
                    <td style={tdStyle}><Badge value={l.entry?.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {Array.isArray(detail.payments) && detail.payments.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <h4 style={{ marginBottom: 6 }}>Payments</h4>
            <table style={tableStyle}>
              <thead><tr>{['Date', 'Amount', 'Method', 'Reference'].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
              <tbody>
                {detail.payments.map(p => (
                  <tr key={p.id}>
                    <td style={tdStyle}>{p.payment_date}</td>
                    <td style={tdStyle}>{fmt(p.amount)}</td>
                    <td style={tdStyle}>{p.payment_method || '—'}</td>
                    <td style={tdStyle}>{p.reference || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {['pending_approval', 'reopened'].includes(detail.status) && (
            <div style={{ display: 'flex', gap: 8 }}>
              <button style={btnPrimary} disabled={busy} onClick={doApprove}>Approve</button>
              {Number(detail.paid_amount) <= 0 && <button style={{ ...btnSecondary, color: '#ef4444' }} disabled={busy} onClick={doReject}>Reject</button>}
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <input type="number" placeholder="Adjustment amount" value={adjustAmount} onChange={e => setAdjustAmount(e.target.value)} style={{ ...inputStyle, width: 150 }} />
            <input placeholder="Reason" value={adjustReason} onChange={e => setAdjustReason(e.target.value)} style={{ ...inputStyle, width: 180 }} />
            <button style={btnSecondary} disabled={busy} onClick={doAdjust}>Apply Adjustment</button>
          </div>

          {canPay && <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <input type="date" value={scheduleDate} onChange={e => setScheduleDate(e.target.value)} style={{ ...inputStyle, width: 160 }} />
            <button style={btnSecondary} disabled={busy} onClick={doSchedule}>Schedule</button>
          </div>}

          {canPay && <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <input type="number" min="0.01" step="0.01" max={detail.remaining_amount} placeholder="Amount" value={payAmount} onChange={e => setPayAmount(e.target.value)} style={{ ...inputStyle, width: 100 }} />
            <input type="date" aria-label="Payment date" value={payDate} onChange={e => setPayDate(e.target.value)} style={{ ...inputStyle, width: 150 }} />
            <input placeholder="Method" value={payMethod} onChange={e => setPayMethod(e.target.value)} style={{ ...inputStyle, width: 110 }} />
            <input placeholder="Reference" value={payReference} onChange={e => setPayReference(e.target.value)} style={{ ...inputStyle, width: 140 }} />
            <button style={btnPrimary} disabled={busy || !validPayment} onClick={doPay}>Record Payment</button>
          </div>}

          <div style={{ display: 'flex', gap: 8 }}>
            {detail.status !== 'paid' && <button style={{ ...btnSecondary, color: '#ef4444' }} disabled={busy} onClick={doCancel}>Cancel</button>}
            <button style={btnSecondary} disabled={busy} onClick={doReopen}>Reopen</button>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
          <button style={btnSecondary} onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 14, color: '#1a1a2e' }}>{value}</div>
    </div>
  );
}
